// Global client-side error logging. Installs window listeners for uncaught
// errors and unhandled promise rejections so any runtime failure (including
// blank-screen 500s) lands in the console with a full stack trace AND is
// forwarded to Lovable's error reporter.

import { reportLovableError } from "./lovable-error-reporting";

type LovableErrorOptions = {
  mechanism?: "manual" | "onerror" | "unhandledrejection" | "react_error_boundary";
  handled?: boolean;
  severity?: "error" | "warning" | "info";
};

let installed = false;

function report(
  error: unknown,
  mechanism: NonNullable<LovableErrorOptions["mechanism"]>,
  context: Record<string, unknown>,
) {
  // Always log raw error so DevTools shows the real stack.
  // eslint-disable-next-line no-console
  console.error(`[${mechanism}]`, error);

  if (typeof window === "undefined") return;
  const events = window.__lovableEvents;
  if (events?.captureException) {
    events.captureException(
      error,
      { source: mechanism, route: window.location.pathname, ...context },
      { mechanism, handled: false, severity: "error" },
    );
  } else {
    // Fall back to the standard reporter wrapper.
    reportLovableError(error, { mechanism, ...context });
  }
}

export function installClientErrorLogger() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    report(event.error ?? new Error(event.message || "Unknown window error"), "onerror", {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason =
      event.reason instanceof Error
        ? event.reason
        : new Error(
            typeof event.reason === "string" ? event.reason : JSON.stringify(event.reason),
          );
    report(reason, "unhandledrejection", {});
  });
}
